import React, { useState } from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import DealRoomListingForm from '../components/Succeedo/DealRoom/DealRoomListingForm';

const LISTING_FOR_EDIT = gql`
  query DealListingForEdit($id: ID!) {
    currentUser { id name succeedo_persona }
    deal_listing(id: $id) {
      id
      industry
      province
      years_in_operation
      employees
      revenue
      adjusted_ebitda
      asking_price
      deal_preferences
      timeline
      business_story
      key_strengths
      photo_urls
      photo_signed_ids
      is_owner
    }
  }
`;

const CURRENT_USER_ONLY = gql`
  query CurrentUserOnly {
    currentUser { id name succeedo_persona }
    listing_draft_suggestions
  }
`;

const SAVE_LISTING = gql`
  mutation SaveDealListing(
    $id: ID
    $industry: String
    $province: String
    $years_in_operation: Int
    $employees: Int
    $revenue: Int
    $adjusted_ebitda: Int
    $asking_price: Int
    $deal_preferences: [String]
    $timeline: String
    $business_story: String
    $key_strengths: String
    $photo_signed_ids: [String]
  ) {
    save_deal_listing(
      id: $id
      industry: $industry
      province: $province
      years_in_operation: $years_in_operation
      employees: $employees
      revenue: $revenue
      adjusted_ebitda: $adjusted_ebitda
      asking_price: $asking_price
      deal_preferences: $deal_preferences
      timeline: $timeline
      business_story: $business_story
      key_strengths: $key_strengths
      photo_signed_ids: $photo_signed_ids
    ) {
      deal { id }
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function csrfToken() {
  if (typeof document === 'undefined') return '';
  const tag = document.querySelector('meta[name="csrf-token"]');
  return tag ? tag.getAttribute('content') : '';
}

async function uploadPhotoFile(file) {
  const formData = new FormData();
  formData.append('photo', file);
  const response = await fetch('/succeedo/deal-room/photos', {
    method: 'POST',
    body: formData,
    headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    throw new Error(json.error || 'Upload failed');
  }
  const { signed_id } = await response.json();
  return signed_id;
}

function FormLoader({ mode, listingId }) {
  const [serverErrors, setServerErrors] = useState([]);
  const isEdit = mode === 'edit' && listingId;

  const { data: editData, loading: loadingExisting } = useQuery(LISTING_FOR_EDIT, {
    variables: { id: listingId },
    skip: !isEdit,
  });
  const { data: userData } = useQuery(CURRENT_USER_ONLY, { skip: isEdit });

  const data = isEdit ? editData : userData;
  const user = data?.currentUser;
  const initialListing = isEdit ? editData?.deal_listing : null;
  // Suggestions only flow in for new listings — edit mode keeps the
  // banner suppressed since the listing already has the user's data.
  const draftSuggestions = isEdit ? null : (userData?.listing_draft_suggestions || null);

  const [saveListing, { loading: saving }] = useMutation(SAVE_LISTING, {
    onCompleted: ({ save_deal_listing }) => {
      if (save_deal_listing?.errors?.length) {
        setServerErrors(save_deal_listing.errors);
        return;
      }
      const id = save_deal_listing?.deal?.id;
      try {
        if (typeof window !== 'undefined' && window.ahoy?.track) {
          window.ahoy.track('succeedo_deal_room_listing_created', { listing_id: id, mode });
        }
      } catch (_e) {}
      if (id) window.location.href = `/succeedo/deal-room/${id}`;
    },
    onError: (e) => setServerErrors([e.message || 'Save failed']),
  });

  const onSave = (form) => {
    setServerErrors([]);
    saveListing({
      variables: {
        id: form.id,
        industry: form.industry,
        province: form.province,
        years_in_operation: form.years_in_operation,
        employees: form.employees,
        revenue: form.revenue,
        adjusted_ebitda: form.adjusted_ebitda,
        asking_price: form.asking_price,
        deal_preferences: form.deal_preferences,
        timeline: form.timeline,
        business_story: form.business_story,
        key_strengths: form.key_strengths,
        photo_signed_ids: form.photo_signed_ids,
      },
    });
  };

  // Wait for the listing query to land before mounting the form. The form's
  // useState initializers only run on first mount, so rendering it before
  // initialListing is populated would lock all inputs to empty strings.
  // Early return must come AFTER all hooks (Rules of Hooks).
  if (isEdit && (loadingExisting || !initialListing)) {
    return (
      <div className="max-w-[800px] mx-auto px-6 py-12">
        <p className="text-sm text-muted-foreground">Loading listing…</p>
      </div>
    );
  }

  // Client-side IDOR safety net. Controller already redirects, but if a
  // non-owner reaches this point (e.g. cached page, race), bounce them to
  // the public detail view rather than mounting the edit form.
  if (isEdit && initialListing && initialListing.is_owner === false) {
    window.location.href = `/succeedo/deal-room/${initialListing.id}`;
    return null;
  }

  return (
    <DealRoomListingForm
      mode={mode}
      initialListing={initialListing}
      saving={saving || loadingExisting}
      serverErrors={serverErrors}
      persona={(user?.succeedo_persona || 'seller').toUpperCase()}
      userName={user?.name || ''}
      userInitials={deriveInitials(user?.name)}
      onSave={onSave}
      onCancel={() => { window.location.href = '/succeedo/deal-room'; }}
      uploadPhoto={uploadPhotoFile}
      draftSuggestions={draftSuggestions}
    />
  );
}

export default function SucceedoDealRoomEditContainer({ mode = 'new', listingId = null }) {
  return (
    <ApolloProvider client={client}>
      <FormLoader mode={mode} listingId={listingId} />
    </ApolloProvider>
  );
}
